import React, { useState, useEffect } from 'react';
import { useEntitlements } from '../store/EntitlementStore';
import api from '../services/api';
import { uploadBrandingAsset } from '../services/s3Upload';
import { Palette, Upload, CheckCircle, XCircle, Info, Eye, ChevronRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import './BrandingPage.css';

/**
 * BrandingPage - White-label branding configuration for staff/admin.
 * Allows customization of company name, colors, logos, domain, and email settings.
 */
export const BrandingPage = () => {
  const { user, hasFeature, hasEntitlement } = useEntitlements();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // File upload states
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLogoDark, setUploadingLogoDark] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    tagline: '',
    logo_url: '',
    logo_dark_url: '',
    favicon_url: '',
    primary_color: '#3B82F6',
    secondary_color: '#6B7280',
    accent_color: '#10B981',
    custom_domain: '',
    email_from_name: '',
    email_from_address: '',
    email_reply_to: '',
    footer_text: '',
    footer_links: {},
    custom_css: '',
    hide_portalr_branding: false,
    enable_custom_domain: false,
    subdomain_enabled: false,
    is_active: true,
  });

  const isStaff = user?.is_staff_user || user?.role === 'staff' || user?.role === 'admin';

  // Check if tenant has branding features enabled
  const hasBrandingAccess = hasFeature('enable_white_label') || hasEntitlement('white_label');

  useEffect(() => {
    if (isStaff) {
      fetchBranding();
    } else {
      setLoading(false);
      setError('Only staff and admins can manage branding configuration.');
    }
  }, [isStaff]);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/branding/');

      if (response.data.results && response.data.results.length > 0) {
        const brandingData = response.data.results[0];
        setBranding(brandingData);
        setFormData({
          company_name: brandingData.company_name || '',
          tagline: brandingData.tagline || '',
          logo_url: brandingData.logo_url || '',
          logo_dark_url: brandingData.logo_dark_url || '',
          favicon_url: brandingData.favicon_url || '',
          primary_color: brandingData.primary_color || '#3B82F6',
          secondary_color: brandingData.secondary_color || '#6B7280',
          accent_color: brandingData.accent_color || '#10B981',
          custom_domain: brandingData.custom_domain || '',
          email_from_name: brandingData.email_from_name || '',
          email_from_address: brandingData.email_from_address || '',
          email_reply_to: brandingData.email_reply_to || '',
          footer_text: brandingData.footer_text || '',
          footer_links: brandingData.footer_links || {},
          custom_css: brandingData.custom_css || '',
          hide_portalr_branding: brandingData.hide_portalr_branding || false,
          enable_custom_domain: brandingData.enable_custom_domain || false,
          subdomain_enabled: brandingData.subdomain_enabled || false,
          is_active: brandingData.is_active !== undefined ? brandingData.is_active : true,
        });
      } else {
        // No branding exists yet
        setBranding(null);
        setIsEditing(true);
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.data?.detail?.includes('No TenantBranding matches')) {
        // No branding exists yet - this is fine
        setBranding(null);
        setIsEditing(true);
      } else {
        setError(err.response?.data?.detail || 'Failed to fetch branding configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    // Clear field error when user modifies the field
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = field === 'favicon_url'
      ? ['image/x-icon', 'image/png']
      : ['image/png', 'image/jpeg', 'image/svg+xml'];

    if (!validTypes.includes(file.type)) {
      setError(`Invalid file type for ${field}. Please upload ${field === 'favicon_url' ? 'ICO or PNG' : 'PNG, JPG, or SVG'}.`);
      return;
    }

    // Validate file size (2MB max for logos, 1MB for favicon)
    const maxSize = field === 'favicon_url' ? 1024 * 1024 : 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File too large. Maximum size is ${field === 'favicon_url' ? '1MB' : '2MB'}.`);
      return;
    }

    // Set uploading state
    if (field === 'logo_url') setUploadingLogo(true);
    if (field === 'logo_dark_url') setUploadingLogoDark(true);
    if (field === 'favicon_url') setUploadingFavicon(true);

    try {
      // Map field name to asset type for S3 upload
      const assetTypeMap = {
        'logo_url': 'logo',
        'logo_dark_url': 'logo_dark',
        'favicon_url': 'favicon',
      };
      const assetType = assetTypeMap[field] || 'logo';

      // Upload to S3 and get URL
      const s3Url = await uploadBrandingAsset(file, assetType, user.tenant.slug);

      setFormData({
        ...formData,
        [field]: s3Url,
      });

      setSuccess(`${field.replace(/_/g, ' ')} uploaded successfully`);
    } catch (err) {
      setError(err.message || `Failed to upload ${field.replace(/_/g, ' ')}`);
    } finally {
      if (field === 'logo_url') setUploadingLogo(false);
      if (field === 'logo_dark_url') setUploadingLogoDark(false);
      if (field === 'favicon_url') setUploadingFavicon(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    try {
      let response;
      if (branding) {
        // Update existing branding
        response = await api.patch(`/v1/branding/${branding.id}/`, formData);
      } else {
        // Create new branding
        response = await api.post('/v1/branding/', formData);
      }

      setBranding(response.data);
      setIsEditing(false);
      setSuccess('Branding configuration saved successfully');
      setFieldErrors({});
    } catch (err) {
      const errorData = err.response?.data || {};
      const newFieldErrors = {};
      let generalError = null;

      // Extract field-specific errors
      Object.entries(errorData).forEach(([field, messages]) => {
        if (Array.isArray(messages)) {
          newFieldErrors[field] = messages;
        } else if (typeof messages === 'string') {
          if (field === 'detail' || field === 'error' || field === 'non_field_errors') {
            generalError = messages;
          } else {
            newFieldErrors[field] = [messages];
          }
        } else if (Array.isArray(messages) && messages.length > 0) {
          newFieldErrors[field] = messages;
        }
      });

      // Handle non_field_errors array
      if (errorData.non_field_errors && Array.isArray(errorData.non_field_errors)) {
        generalError = errorData.non_field_errors.join('; ');
      }

      setFieldErrors(newFieldErrors);
      setError(generalError || (Object.keys(newFieldErrors).length > 0 
        ? 'Please fix the errors below' 
        : 'Failed to save branding configuration'));
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!branding || !branding.custom_domain) {
      setError('Please save a custom domain first');
      return;
    }

    setVerifyingDomain(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post(`/v1/branding/${branding.id}/verify-domain/`);

      if (response.data.verified) {
        setSuccess('Domain verified successfully!');
        fetchBranding(); // Refresh to get updated verification status
      } else {
        setError(response.data.error || 'Domain verification failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify domain');
    } finally {
      setVerifyingDomain(false);
    }
  };

  const validateHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  if (loading) {
    return (
      <div className="branding-page">
        <div className="loading">Loading branding configuration...</div>
      </div>
    );
  }

  if (!isStaff) {
    return (
      <div className="branding-page">
        <div className="error-banner">{error}</div>
      </div>
    );
  }

  return (
    <div className="branding-page">
      <PageHeader
        icon={Palette}
        title="Branding Configuration"
        subtitle="Customize logos, colors, and domain settings for your portal."
        primaryAction={branding && !isEditing ? (
          <button
            className="btn-primary"
            onClick={() => setIsEditing(true)}
          >
            Edit Branding
          </button>
        ) : null}
      />

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Show upgrade message if branding is not enabled */}
      {!hasBrandingAccess && (
        <div className="alert alert-info">
          <Info size={20} />
          <div>
            <strong>Branding features are not enabled for your account.</strong>
            <p>To customize your portal's branding, upload logos, set custom colors, and configure a custom domain, please purchase a branding add-on from <a href="/settings?tab=plans">Settings → Plans & Modules</a>.</p>
          </div>
        </div>
      )}

      {/* Only show branding configuration if feature is enabled */}
      {hasBrandingAccess && (
        <>
          {!branding && !isEditing ? (
            <div className="portalr-emptyState">
              <div className="portalr-emptyStateIcon" aria-hidden="true">
                <Palette size={20} />
              </div>
              <div className="portalr-emptyStateTitle">No branding configuration yet</div>
              <p className="portalr-emptyStateDesc">
                Create your first branding setup to customize logos and colors for your portal.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className="portalr-btn portalr-btn--primary"
                  onClick={() => setIsEditing(true)}
                >
                  Create branding configuration
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSave} className="branding-form">
          {/* Basic Information */}
          <section className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Your Company Name"
                className={fieldErrors.company_name ? 'error' : ''}
              />
              {fieldErrors.company_name && (
                <div className="field-error">{fieldErrors.company_name.join(', ')}</div>
              )}
              <small className="form-help">Displayed in the portal header and emails</small>
            </div>

            <div className="form-group">
              <label>Tagline</label>
              <input
                type="text"
                name="tagline"
                value={formData.tagline}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Your company tagline"
                className={fieldErrors.tagline ? 'error' : ''}
              />
              {fieldErrors.tagline && (
                <div className="field-error">{fieldErrors.tagline.join(', ')}</div>
              )}
              <small className="form-help">Short slogan or tagline (optional)</small>
            </div>
          </section>

          {/* Logos */}
          <section className="form-section">
            <h2>Logos & Assets</h2>

            <div className="form-group">
              <label>Primary Logo</label>
              <div className="file-upload-group">
                {formData.logo_url && (
                  <img src={formData.logo_url} alt="Logo preview" className="logo-preview" />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={(e) => handleFileUpload(e, 'logo_url')}
                  disabled={!isEditing || uploadingLogo}
                />
                {uploadingLogo && <span className="uploading">Uploading...</span>}
              </div>
              <small className="form-help">PNG, JPG, or SVG. Max 2MB. Recommended: 200x50px</small>
            </div>

            <div className="form-group">
              <label>Dark Mode Logo (Optional)</label>
              <div className="file-upload-group">
                {formData.logo_dark_url && (
                  <img src={formData.logo_dark_url} alt="Dark logo preview" className="logo-preview dark" />
                )}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={(e) => handleFileUpload(e, 'logo_dark_url')}
                  disabled={!isEditing || uploadingLogoDark}
                />
                {uploadingLogoDark && <span className="uploading">Uploading...</span>}
              </div>
              <small className="form-help">Used when portal is in dark mode</small>
            </div>

            <div className="form-group">
              <label>Favicon</label>
              <div className="file-upload-group">
                {formData.favicon_url && (
                  <img src={formData.favicon_url} alt="Favicon preview" className="favicon-preview" />
                )}
                <input
                  type="file"
                  accept="image/x-icon,image/png"
                  onChange={(e) => handleFileUpload(e, 'favicon_url')}
                  disabled={!isEditing || uploadingFavicon}
                />
                {uploadingFavicon && <span className="uploading">Uploading...</span>}
              </div>
              <small className="form-help">ICO or PNG. 32x32px or 64x64px</small>
            </div>
          </section>

          {/* Color Scheme */}
          <section className="form-section">
            <h2>Color Scheme</h2>

            <div className="color-inputs-row">
              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <input
                    type="text"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="#3B82F6"
                    className="hex-input"
                  />
                </div>
                {fieldErrors.primary_color && (
                  <div className="field-error">{fieldErrors.primary_color.join(', ')}</div>
                )}
                <small className="form-help">Main brand color</small>
              </div>

              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <input
                    type="text"
                    name="secondary_color"
                    value={formData.secondary_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="#6B7280"
                    className="hex-input"
                  />
                </div>
                {fieldErrors.secondary_color && (
                  <div className="field-error">{fieldErrors.secondary_color.join(', ')}</div>
                )}
                <small className="form-help">Secondary accent color</small>
              </div>

              <div className="form-group">
                <label>Accent Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    name="accent_color"
                    value={formData.accent_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                  <input
                    type="text"
                    name="accent_color"
                    value={formData.accent_color}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="#10B981"
                    className="hex-input"
                  />
                </div>
                {fieldErrors.accent_color && (
                  <div className="field-error">{fieldErrors.accent_color.join(', ')}</div>
                )}
                <small className="form-help">Highlights and CTAs</small>
              </div>
            </div>

            <div className="color-preview">
              <h3>Color Preview</h3>
              <div className="color-preview-boxes">
                <div className="color-box" style={{ backgroundColor: formData.primary_color }}>
                  <span>Primary</span>
                </div>
                <div className="color-box" style={{ backgroundColor: formData.secondary_color }}>
                  <span>Secondary</span>
                </div>
                <div className="color-box" style={{ backgroundColor: formData.accent_color }}>
                  <span>Accent</span>
                </div>
              </div>
            </div>
          </section>

          {/* Subdomain Routing */}
          <section className="form-section">
            <h2>Subdomain Routing</h2>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="subdomain_enabled"
                  checked={formData.subdomain_enabled}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                Enable Subdomain Routing
              </label>
              <small className="form-help">Access your portal via {branding?.subdomain || '{tenant-slug}.portalrapp.com'}</small>
            </div>

            {formData.subdomain_enabled && branding?.subdomain && (
              <div className="dns-instructions">
                <Info size={16} />
                <div>
                  <strong>DNS Configuration:</strong>
                  <p>Your portal will be accessible at:</p>
                  <code>{branding.subdomain}</code>
                  <p style={{ marginTop: '0.5rem' }}>
                    <strong>Note:</strong> If using wildcard DNS, no additional configuration is needed. 
                    Otherwise, configure your DNS provider to point subdomains to <code>portalrapp.com</code>
                  </p>
                </div>
              </div>
            )}
          </section>

          {/* Custom Domain */}
          <section className="form-section">
            <h2>Custom Domain</h2>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="enable_custom_domain"
                  checked={formData.enable_custom_domain}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                Enable Custom Domain
              </label>
              <small className="form-help">Requires Professional or Enterprise plan</small>
            </div>

            {formData.enable_custom_domain && (
              <>
                <div className="form-group">
                  <label>Custom Domain</label>
                  <input
                    type="text"
                    name="custom_domain"
                    value={formData.custom_domain}
                    onChange={handleChange}
                    disabled={!isEditing}
                    placeholder="portal.yourcompany.com"
                    className={fieldErrors.custom_domain ? 'error' : ''}
                  />
                  {fieldErrors.custom_domain && (
                    <div className="field-error">{fieldErrors.custom_domain.join(', ')}</div>
                  )}
                  <small className="form-help">Domain only (no https:// or path)</small>
                </div>

                {branding?.custom_domain && (
                  <div className={`domain-verification-status ${branding.custom_domain_verified ? 'verified' : 'unverified'}`}>
                    {branding.custom_domain_verified ? (
                      <>
                        <CheckCircle size={20} />
                        <span>Domain Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} />
                        <span>Domain Not Verified</span>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={handleVerifyDomain}
                          disabled={verifyingDomain}
                        >
                          {verifyingDomain ? 'Verifying...' : 'Verify Domain'}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {branding?.custom_domain && !branding?.custom_domain_verified && (
                  <div className="dns-instructions">
                    <Info size={16} />
                    <div>
                      <strong>DNS Configuration Required:</strong>
                      <p>Add the following TXT record to your DNS:</p>
                      <code>_portalr-verify.{branding.custom_domain}</code>
                      <p>Value: <code>{branding.custom_domain_verification_token}</code></p>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Email Branding */}
          <section className="form-section">
            <h2>Email Branding</h2>

            <div className="form-group">
              <label>Email From Name</label>
              <input
                type="text"
                name="email_from_name"
                value={formData.email_from_name}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Acme Corp Support"
              />
              <small className="form-help">Name displayed in email "From" field</small>
            </div>

            <div className="form-group">
              <label>Email From Address</label>
              <input
                type="email"
                name="email_from_address"
                value={formData.email_from_address}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="support@yourcompany.com"
              />
              <small className="form-help">Must match custom domain if set</small>
            </div>

            <div className="form-group">
              <label>Email Reply-To</label>
              <input
                type="email"
                name="email_reply_to"
                value={formData.email_reply_to}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="replies@yourcompany.com"
              />
              <small className="form-help">Where replies are sent (optional)</small>
            </div>
          </section>

          {/* Advanced Settings */}
          <section className="form-section">
            <h2>Advanced Settings</h2>

            <div className="form-group">
              <label>Footer Text</label>
              <textarea
                name="footer_text"
                value={formData.footer_text}
                onChange={handleChange}
                disabled={!isEditing}
                rows="3"
                placeholder="Custom footer text (supports markdown)"
              />
            </div>

            <div className="form-group">
              <label>Custom CSS</label>
              <textarea
                name="custom_css"
                value={formData.custom_css}
                onChange={handleChange}
                disabled={!isEditing}
                rows="6"
                placeholder="/* Custom CSS (max 10KB) */"
                className={`code-input ${fieldErrors.custom_css ? 'error' : ''}`}
              />
              {fieldErrors.custom_css && (
                <div className="field-error">{fieldErrors.custom_css.join(', ')}</div>
              )}
              <small className="form-help">Advanced styling. Use with caution.</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="hide_portalr_branding"
                  checked={formData.hide_portalr_branding}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                Hide "Powered by Portalr" Branding
              </label>
              <small className="form-help">Requires Enterprise plan</small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
                Branding Active
              </label>
              <small className="form-help">Whether to apply this branding configuration</small>
            </div>
          </section>

          {isEditing && (
            <div className="form-actions">
              {branding && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    fetchBranding();
                  }}
                >
                  Cancel
                </button>
              )}
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : branding ? 'Save Changes' : 'Create Branding'}
              </button>
            </div>
          )}
        </form>
          )}
        </>
      )}
    </div>
  );
};
